import * as React from 'react';
import { FocusScope, useInteractOutside, usePreventScroll } from 'react-aria';

import {
    Backdrop,
    type IconDefinition,
    KeyboardShortcut,
    LoadingPanel,
    Portal,
    renderIcon,
} from '@gitbook/spine-react';

export interface SearchModalProps {
    /** The value of the search query. */
    query: string;

    /** Placeholder for the search input */
    queryPlaceholder?: string;

    /** Callback when the search query is changed. */
    onQueryChange: (query: string) => void;

    /** Callback when the user is submitting the query by pressing enter. */
    onSubmitQuery?: (query: string) => void;

    /** Callback when the user is closing the modal. */
    onClose: () => void;

    /** Inner body */
    children: React.ReactNode;

    /** Layout elements */
    inputIcon?: IconDefinition;
    leftButton?: React.ReactNode;
    rightButton?: React.ReactNode;

    /** Prompt to enable the search AI feature with toggle or upgrade button */
    enableSearchAIPrompt?: React.ReactNode;
}

export interface SearchModalRef {
    focus(): void;
    select(): void;
}

/**
 * Body of the search modal. Split in a different component so that the useEffect are called after the mount in the portal element.
 */
export function SearchModal({
    ref,
    ...props
}: SearchModalProps & { ref?: React.Ref<SearchModalRef> }): React.ReactElement {
    const {
        query,
        onQueryChange,
        onSubmitQuery,
        queryPlaceholder = 'Search…',
        onClose,
        children,
        enableSearchAIPrompt,
        inputIcon: InputIconProp,
        leftButton,
        rightButton,
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const backdropRef = React.useRef<HTMLDivElement>(null);
    usePreventScroll();
    useInteractOutside({
        ref: contentRef,
        onInteractOutside: (event) => {
            // We only want to close the modal if the interaction outside is on its
            // backdrop. In other words, the event is triggered from within the dialog
            // but not within the dialog's content.
            //
            // We are ignoring interactions outside the dialog element because that
            // can include interactions in portals - such as palette menus.
            if (
                event.target instanceof Element &&
                backdropRef.current &&
                (backdropRef.current === event.target || backdropRef.current.contains(event.target))
            ) {
                onClose();
            }
        },
    });

    React.useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
        select: () => {
            inputRef.current?.select();
        },
    }));

    /**
     * Focus the input when the search view is mounted and select the entire text.
     */
    React.useEffect(() => {
        inputRef.current?.select();
    }, []);

    return (
        <Portal>
            <Backdrop
                ref={backdropRef}
                isEntering
                className="z-(--z-index-popover) flex flex-col items-center p-4 md:pt-[8vh] md:pb-[8vh]"
            >
                <FocusScope autoFocus restoreFocus contain>
                    <div
                        ref={contentRef}
                        data-testid="palette"
                        className="mx-auto flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-lg border bg-base shadow-elevated animate-in fade-in zoom-in-90"
                    >
                        {/* Text input wrapper */}
                        <div className="flex items-center border-b border-neutral-subtle bg-neutral-surface-subtle px-4">
                            {leftButton}
                            {InputIconProp
                                ? renderIcon(InputIconProp, {
                                      size: 18,
                                      className: 'text-neutral-low',
                                  })
                                : null}
                            <div className="flex w-full flex-1">
                                <input
                                    ref={inputRef}
                                    value={query}
                                    placeholder={queryPlaceholder}
                                    onChange={(event) => {
                                        onQueryChange(event.currentTarget.value);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Escape') {
                                            onClose();
                                            return;
                                        }
                                        if (event.key === 'Enter') {
                                            onSubmitQuery?.(query);
                                            return;
                                        }
                                    }}
                                    className="flex-1 border-none bg-neutral-surface-subtle p-4 text-ui-heading-small text-base placeholder:text-muted"
                                />
                            </div>
                            {rightButton}
                        </div>
                        {/* Search results */}
                        <div className="flex scrollbar flex-1 flex-col overflow-y-auto scroll-gradient-mask-y">
                            <React.Suspense fallback={<LoadingPanel size="large" />}>
                                {children}
                            </React.Suspense>
                        </div>
                        <SearchModalFooter enableSearchAIPrompt={enableSearchAIPrompt} />
                    </div>
                </FocusScope>
            </Backdrop>
        </Portal>
    );
}

export function SearchModalFooter(props: {
    enableSearchAIPrompt: React.ReactNode;
}): React.ReactElement {
    const { enableSearchAIPrompt } = props;
    return (
        <div className="flex items-center justify-between border-t border-neutral-subtle px-3 py-2 text-ui-small text-neutral-low">
            <div className="hidden items-center gap-6 text-ui-small text-neutral-low md:flex">
                <div className="flex items-center gap-2">
                    <span>Select</span>
                    <KeyboardShortcut shortcut="↑" size="xsmall" />
                    <KeyboardShortcut shortcut="↓" size="xsmall" />
                </div>
                <div className="flex items-center gap-2">
                    <span>Open</span>
                    <KeyboardShortcut shortcut="enter" size="xsmall" />
                </div>
                <div className="flex items-center gap-2">
                    <span>Close</span>
                    <KeyboardShortcut shortcut="esc" size="xsmall" />
                </div>
            </div>
            {enableSearchAIPrompt ? enableSearchAIPrompt : null}
        </div>
    );
}
